// How a printed photo book looks, and the plan of its pages. The builder's preview and the PDF (print/storybook.ts)
// both draw from the same plan and the same measures here, so the book turned on screen is the book that's printed.
// A look is a handful of plain choices; the named looks are just starting points, and changing any choice makes it
// the maker's own. Nothing here changes a photo: edge to edge crops only what's printed, from the original.
import { printedPages } from '../../../shared/print'
import { PHOTO_BOOK, PHOTO_PAGE, readDesign } from '../../../shared/printDesign'
import type { Album } from '../albums'
import { bookPages, type BookPage, type PhotoPageEdits } from '../storybook'

export type Edges = (typeof PHOTO_BOOK.edges)[number]

export type Look = {
  // The most photos a page holds; a chapter's photos are shared evenly over the pages it needs.
  most: (typeof PHOTO_BOOK.most)[number]
  edges: Edges
  type: (typeof PHOTO_BOOK.type)[number]
  captions: boolean
  paper: (typeof PHOTO_BOOK.paper)[number]
  cover: (typeof PHOTO_BOOK.cover)[number]
  // How the photos print: as they are, black and white, or sepia.
  photos: (typeof PHOTO_BOOK.photos)[number]
}

export const LOOKS = {
  // The book as it reads in the storybook's Book view, and as it printed before the builder.
  classic: {
    name: 'Classic',
    blurb: 'Whole photos, up to four a page',
    look: {
      most: 4,
      edges: 'border',
      type: 'modern',
      captions: true,
      paper: 'white',
      cover: 'photo',
      photos: 'color',
    },
  },
  gallery: {
    name: 'Gallery',
    blurb: 'One photo a page, wide margins',
    look: {
      most: 1,
      edges: 'wide',
      type: 'traditional',
      captions: true,
      paper: 'white',
      cover: 'words',
      photos: 'color',
    },
  },
  bleed: {
    name: 'Edge to edge',
    blurb: 'Each photo fills its page',
    look: { most: 1, edges: 'bleed', type: 'modern', captions: true, paper: 'white', cover: 'photo', photos: 'color' },
  },
  collage: {
    name: 'Collage',
    blurb: 'Up to four a page, tight gaps',
    look: {
      most: 4,
      edges: 'thin',
      type: 'modern',
      captions: false,
      paper: 'white',
      cover: 'window',
      photos: 'color',
    },
  },
} as const satisfies Record<string, { name: string; blurb: string; look: Look }>
export type LookId = keyof typeof LOOKS
export const LOOK_IDS = Object.keys(LOOKS) as LookId[]
export const CLASSIC: Look = LOOKS.classic.look

export const sameLook = (a: Look, b: Look) => (Object.keys(a) as (keyof Look)[]).every((k) => a[k] === b[k])

// Margins and the gap between photos, in inches from the trimmed edge. Edge to edge runs photos out past the cut into
// the bleed, with a hairline between them.
export const MARGINS: Record<Edges, { top: number; side: number; bottom: number; gap: number }> = {
  bleed: { top: 0, side: 0, bottom: 0, gap: 0.06 },
  thin: { top: 0.4, side: 0.4, bottom: 0.4, gap: 0.12 },
  border: { top: 0.8, side: 0.75, bottom: 0.95, gap: 0.3 },
  wide: { top: 1.4, side: 1.4, bottom: 1.6, gap: 0.5 },
}
// Edge to edge and thin fill each photo's share of the page, cropping what's printed; a border or wide margins keep
// every photo whole.
export const fills = (edges: Edges) => edges === 'bleed' || edges === 'thin'

// A look read back, or nothing if it isn't a whole, known look.
export const parseLook = (value: unknown): Look | null =>
  readDesign(PHOTO_BOOK, { from: 'classic', look: value })?.look ?? null

// The design: a look, and any pages given choices of their own, each by the photo it starts with.
export type Chosen = { from: LookId; look: Look; pages?: PhotoPageEdits }
export type PageEdit = PhotoPageEdits[string]
export const readChosen = (value: unknown): Chosen | null => readDesign(PHOTO_BOOK, value, PHOTO_PAGE)

// The look a page is drawn in: the book's, with the page's own edges when it has them.
export const lookOn = (look: Look, page: { kind: string; edges?: Edges }): Look =>
  page.kind === 'photos' && page.edges ? { ...look, edges: page.edges } : look

// The interior's pages in order: the title page (and, in a bound book, its blank back), the storybook's pages with
// the look's photos a page, ruled notes pages to reach the binding's fewest pages, and the closing page.
export type PlanPage =
  { kind: 'title' } | { kind: 'blank' } | Exclude<BookPage, { kind: 'cover' }> | { kind: 'notes' } | { kind: 'closing' }
export type Plan = { pages: PlanPage[]; added: number }

export function photoBookPlan(
  album: Pick<Album, 'description' | 'photos'>,
  look: Pick<Look, 'most'>,
  layout: { minPages: number; bound: boolean },
  edits?: PhotoPageEdits,
): Plan {
  const book = bookPages(album, look.most, edits).filter(
    (x): x is Exclude<BookPage, { kind: 'cover' }> => x.kind !== 'cover',
  )
  const pages: PlanPage[] = [{ kind: 'title' }]
  if (layout.bound) pages.push({ kind: 'blank' })
  pages.push(...book)
  const planned = pages.length + 1
  const total = printedPages(planned, layout)
  while (pages.length + 1 < total) pages.push({ kind: 'notes' })
  pages.push({ kind: 'closing' })
  return { pages, added: total - planned }
}
